import * as React from "react";
import { ArrowLeft } from "Components/ui/icons";

import { Button, EnvBadge, Logo, ThemeToggle } from "Components/ui";
import { cn } from "utils/cn";
import BrandPanel from "./BrandPanel";

/** Split screen on large displays (brand panel + form); form only, with the logo on top, below. */
export const AuthLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-background text-foreground lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
    <BrandPanel className="hidden lg:flex lg:sticky lg:top-0 lg:h-screen" />
    <main className="flex min-h-screen flex-col px-5 py-6 sm:px-10 sm:py-8">
      <header className="flex items-center justify-between gap-3 lg:justify-end">
        <Logo className="lg:hidden" />
        <div className="flex items-center gap-2">
          <EnvBadge className="lg:hidden" />
          {/* Labelled where there's room; the compact switch on narrow phones. */}
          <ThemeToggle variant="segmented" className="hidden sm:inline-flex" />
          <ThemeToggle className="sm:hidden" />
        </div>
      </header>
      <div className="flex flex-1 items-center justify-center py-10">
        <div className="w-full max-w-[25rem]">{children}</div>
      </div>
      <footer className="text-center text-xs leading-5 text-muted-foreground">
        Access is limited to Meta16 Labs staff. Every sign-in is recorded in the audit log.
      </footer>
    </main>
  </div>
);

interface StepHeaderProps {
  /** Shown as "Step 1 of 2" with a segmented bar. */
  step?: { current: number; total: number; label: string };
  title: string;
  description?: React.ReactNode;
  /** Link-style button above the header, e.g. "Back to sign in". */
  back?: { label: string; onClick: () => void };
}

export const StepHeader: React.FC<StepHeaderProps> = ({ step, title, description, back }) => (
  <div className="mb-7 space-y-5">
    {back && (
      <Button
        variant="ghost"
        size="sm"
        onClick={back.onClick}
        leftIcon={<ArrowLeft aria-hidden="true" />}
        className="-ml-3 text-sm text-muted-foreground hover:text-foreground"
      >
        {back.label}
      </Button>
    )}
    {step && (
      <div className="flex items-center gap-3">
        <div aria-hidden="true" className="flex gap-1.5">
          {Array.from({ length: step.total }, (_, index) => (
            <span
              key={index}
              className={cn(
                "h-1 w-8 rounded-full transition-colors duration-base",
                index < step.current ? "bg-primary" : "bg-border"
              )}
            />
          ))}
        </div>
        <p className="text-xs font-medium text-muted-foreground">
          {step.label}
          <span className="text-subtle-foreground">
            {" "}
            · Step {step.current} of {step.total}
          </span>
        </p>
      </div>
    )}
    <div className="space-y-2">
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
      {description && <p className="text-sm leading-6 text-muted-foreground">{description}</p>}
    </div>
  </div>
);
